import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconRefresh, IconPhone, IconBuilding } from '@tabler/icons-react';
import { IconLoader2 } from '@tabler/icons-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

interface IotCustomer {
  id: number;
  i_customer: number;
  company_name: string | null;
  code: string | null;
  company_registration_number: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  suburb: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  id_number: string | null;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  xero_contact_id: string | null;
}

interface IotAccount {
  id: number;
  i_customer: number;
  package_id: number | null;
  i_account: number;
  msisdn: string;
  alias: string | null;
  voice_balance: number;
  sms_balance: number;
  data_balance: number;
  ip: string | null;
  created_at: string;
  updated_at: string;
  package_name?: string | null;
  data_value?: number | null;
  voice_value?: number | null;
  sms_value?: number | null;
  data_reload?: number | null;
  voice_reload?: number | null;
  sms_reload?: number | null;
  price?: number | null;
  company_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email_address?: string | null;
}

interface IotPackage {
  id: number;
  package_name: string;
  data_value: number;
  voice_value: number;
  sms_value: number;
  data_reload: number;
  voice_reload: number;
  sms_reload: number;
  price: number;
}

export function IotPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('customers');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Customer list state
  const [customerSearch, setCustomerSearch] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [customers, setCustomers] = useState<IotCustomer[]>([]);

  // Account search state
  const [accountQuery, setAccountQuery] = useState('');
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accounts, setAccounts] = useState<IotAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<IotAccount | null>(null);

  // Load all customers on mount and when search changes
  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: loadCustomers is not wrapped in useCallback
  }, []);

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    setCustomerError('');
    try {
      const searchParam = customerSearch.trim() ? `?search=${encodeURIComponent(customerSearch.trim())}` : '';
      const response = await api.get(`/iot/customers${searchParam}`);
      setCustomers(response.data.customers || []);
    } catch (err: any) {
      setCustomerError(err.response?.data?.message || 'Failed to load customers');
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Load customers when search changes (debounced)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      loadCustomers();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadCustomers is not wrapped in useCallback; customerSearch is the intended trigger
  }, [customerSearch]);

  const handleSearchAccount = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!accountQuery.trim()) {
      setAccountError('Please enter a search query');
      return;
    }

    setLoadingAccount(true);
    setAccountError('');
    setAccounts([]);
    setSelectedAccount(null);

    try {
      const response = await api.get(`/iot/search-account?query=${encodeURIComponent(accountQuery.trim())}`);
      setAccounts(response.data.accounts || []);
      if (response.data.accounts && response.data.accounts.length > 0) {
        setSelectedAccount(response.data.accounts[0]);
      }
    } catch (err: any) {
      setAccountError(err.response?.data?.message || 'Failed to search accounts');
      setAccounts([]);
    } finally {
      setLoadingAccount(false);
    }
  };

  const handleCustomerClick = (customer: IotCustomer) => {
    navigate(`/iot/customer/${customer.i_customer}`);
  };

  const handleAccountClick = (account: IotAccount) => {
    setSelectedAccount(account);
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return `R ${value.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <PageHeader
        title="IoT Management"
        subtitle="Search and manage IoT customers, accounts, and packages"
      />

      <MainCard className="mb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="customers">
              <IconBuilding size={16} />
              Customers
            </TabsTrigger>
            <TabsTrigger value="accounts">
              <IconPhone size={16} />
              Search Accounts
            </TabsTrigger>
          </TabsList>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <div className="p-4">
              <div className="rounded-lg border border-border p-4 mb-4">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="flex-1 w-full">
                    <Label htmlFor="customer-search">Search Customers</Label>
                    <Input
                      id="customer-search"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Search by i_customer, company name, or person name"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Type to search customers (searches automatically after 500ms)
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={loadCustomers}
                    disabled={loadingCustomers}
                    className="min-w-[120px]"
                  >
                    <IconRefresh size={16} />
                    Refresh
                  </Button>
                </div>
                {customerError && (
                  <Alert variant="destructive" className="mt-3">
                    <AlertDescription>{customerError}</AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Customer List */}
              {loadingCustomers ? (
                <div className="flex justify-center p-8">
                  <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : customers.length > 0 ? (
                <div className="rounded-lg border border-border/60 overflow-hidden">
                  <div className="border-b border-border/60 px-4 py-2.5">
                    <h3 className="text-sm font-semibold">
                      All Customers ({customers.length})
                    </h3>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>i_customer</TableHead>
                        <TableHead>Company Name</TableHead>
                        <TableHead>Contact Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customers.map((customer) => (
                        <TableRow
                          key={customer.id}
                          className="cursor-pointer"
                          onClick={() => handleCustomerClick(customer)}
                        >
                          <TableCell>{customer.i_customer}</TableCell>
                          <TableCell>{customer.company_name || 'N/A'}</TableCell>
                          <TableCell>
                            {`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'N/A'}
                          </TableCell>
                          <TableCell>{customer.email_address || 'N/A'}</TableCell>
                          <TableCell>{customer.code || 'N/A'}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCustomerClick(customer);
                              }}
                            >
                              View Accounts
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <Alert>
                  <AlertDescription>No customers found</AlertDescription>
                </Alert>
              )}
            </div>
          </TabsContent>

          {/* Account Search Tab */}
          <TabsContent value="accounts">
            <div className="p-4">
              <div className="rounded-lg border border-border p-4 mb-4">
                <form onSubmit={handleSearchAccount} className="flex flex-col gap-3">
                  <div>
                    <Label htmlFor="account-search">Search Account</Label>
                    <Input
                      id="account-search"
                      value={accountQuery}
                      onChange={(e) => setAccountQuery(e.target.value)}
                      placeholder="Enter MSISDN, alias, or i_account"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Search by MSISDN, alias, or i_account number
                    </p>
                  </div>
                  {accountError && (
                    <Alert variant="destructive">
                      <AlertDescription>{accountError}</AlertDescription>
                    </Alert>
                  )}
                  <Button type="submit" disabled={loadingAccount} className="w-full">
                    {loadingAccount ? (
                      <IconLoader2 size={16} className="animate-spin" />
                    ) : (
                      <IconSearch size={16} />
                    )}
                    {loadingAccount ? 'Searching...' : 'Search Account'}
                  </Button>
                </form>
              </div>

              {/* Account Results */}
              {accounts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4">
                  <div className="rounded-lg border border-border/60 overflow-hidden">
                    <div className="border-b border-border/60 px-4 py-2.5">
                      <h3 className="text-sm font-semibold">
                        Accounts ({accounts.length})
                      </h3>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {accounts.map((account) => (
                        <div
                          key={account.id}
                          onClick={() => handleAccountClick(account)}
                          className={cn(
                            'p-3 cursor-pointer border-b border-border hover:bg-muted/50 transition-colors',
                            selectedAccount?.id === account.id && 'bg-muted'
                          )}
                        >
                          <p className="font-semibold">{account.msisdn}</p>
                          <p className="text-sm text-muted-foreground">
                            {account.alias || 'No alias'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {account.company_name || `${account.first_name || ''} ${account.last_name || ''}`.trim() || 'N/A'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedAccount && (
                    <Card>
                      <CardContent className="p-5">
                        <h3 className="text-lg font-semibold mb-2">Account Details</h3>
                        <Separator className="mb-4" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">MSISDN</p>
                            <p className="font-semibold">{selectedAccount.msisdn}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Alias</p>
                            <p>{selectedAccount.alias || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">i_account</p>
                            <p>{selectedAccount.i_account}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">i_customer</p>
                            <p>{selectedAccount.i_customer}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Customer</p>
                            <p>
                              {selectedAccount.company_name || 
                               `${selectedAccount.first_name || ''} ${selectedAccount.last_name || ''}`.trim() || 
                               'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Email</p>
                            <p>{selectedAccount.email_address || 'N/A'}</p>
                          </div>
                        </div>

                        <Separator className="my-4" />
                        <h4 className="text-sm font-semibold mb-3">Package Information</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Package Name</p>
                            <p>{selectedAccount.package_name || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Package Price</p>
                            <p>{formatCurrency(selectedAccount.price)}</p>
                          </div>
                        </div>

                        <Separator className="my-4" />
                        <h4 className="text-sm font-semibold mb-3">Balances</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Data Balance</p>
                            <p className="text-lg font-semibold text-primary">{formatCurrency(selectedAccount.data_balance)}</p>
                            {selectedAccount.data_value && (
                              <p className="text-xs text-muted-foreground">
                                Package: {formatCurrency(selectedAccount.data_value)}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Voice Balance</p>
                            <p className="text-lg font-semibold text-primary">{formatCurrency(selectedAccount.voice_balance)}</p>
                            {selectedAccount.voice_value && (
                              <p className="text-xs text-muted-foreground">
                                Package: {formatCurrency(selectedAccount.voice_value)}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">SMS Balance</p>
                            <p className="text-lg font-semibold text-primary">{formatCurrency(selectedAccount.sms_balance)}</p>
                            {selectedAccount.sms_value && (
                              <p className="text-xs text-muted-foreground">
                                Package: {formatCurrency(selectedAccount.sms_value)}
                              </p>
                            )}
                          </div>
                        </div>

                        <Separator className="my-4" />
                        <h4 className="text-sm font-semibold mb-3">Additional Information</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">IP Address</p>
                            <p>{selectedAccount.ip || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Created At</p>
                            <p>{formatDate(selectedAccount.created_at)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Updated At</p>
                            <p>{formatDate(selectedAccount.updated_at)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </MainCard>
    </div>
  );
}
